import { IScope, IDirective, IAugmentedJQuery, IAttributes, INgModelController, IDirectiveFactory, noop } from "angular";

import { IAssignee, AssigneeType, IAssigneeIdMap } from "../models";
import { IUser } from "@gtmhub/users/models";
import { TeamService } from "@gtmhub/teams/teamService";
import { AnalyticsService } from "@gtmhub/core/analytics";
import { ITeam } from "@gtmhub/teams";
import { IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import colors from "wwwroot/json/colors.json";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { AssigneeSelectorModal } from "../assignee-selector-modal";
import { INgRedux } from "@gtmhub/state-management";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { getCurrentUserId } from "@gtmhub/users";
import { IAssigneesStoreState } from "../redux/assignee-reducer";
import { AssigneeActions } from "../redux/assignee-actions";

interface IInviteOption {
  type: "invite";
  email: string;
}

interface ICreateTeamOption {
  type: "create_team";
  name: string;
}

type IAssigneeOption = IAssignee | IInviteOption | ICreateTeamOption;

interface IAssigneeSelectorScope extends IScope, IExtendedRootScopeService {
  assigneesIdMap: IAssigneeIdMap;
  assigneesFetched: boolean;
  assigneesError: IUIError;
  assignToMe(): void;
  type?: AssigneeType;
  assignee: IAssignee;
  edit?: boolean;
  placeholder?: string;
  disabled?: boolean;
  search: {
    assignee?: string;
  };
  invitation: {
    email: string;
  };
  assigneeOptions(filterByName?: string): IAssigneeOption[];
  selectOption(option: IAssigneeOption): void;
  changeSelectedAssignee(): void;
  undoSelectingAssignee(): void;
  canAssignToMe(): boolean;
  openAssigneesList(): void;
  closeInvitation(): void;
  userInvited(user: IUser): void;
  selected(args: { assignee: IAssignee }): void;
}

const maxSearchResults = 10;

const randomColor = () => colors.general[Math.floor(Math.random() * colors.general.length)];

const bindStateToScope = (state: IAssigneesStoreState) => {
  const { map: assigneesIdMap, isFetched: assigneesFetched, error: assigneesError } = state.assignees;

  return {
    assigneesIdMap,
    assigneesFetched,
    assigneesError,
  };
};

export class AssigneeSelector implements IDirective {
  public scope = {
    type: "<",
    selected: "&",
    placeholder: "@?",
    edit: "=?",
    hideLinks: "<",
    disabled: "=",
  };
  public restrict = "E";
  public templateUrl = require("../views/assignee-selector.jade");
  public require = "ngModel";

  constructor(
    private $rootScope: IGtmhubRootScopeService,
    private teamService: TeamService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private analyticsService: AnalyticsService,
    private assigneeSelectorModal: AssigneeSelectorModal,
    private assigneeActions: AssigneeActions,
    private $ngRedux: INgRedux
  ) {}

  public link(scope: IAssigneeSelectorScope, element: IAugmentedJQuery, attrs: IAttributes, ctrl: INgModelController): void {
    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(scope);
    scope.$on("$destroy", unsubscribe);

    const canInvitePeople = this.$rootScope.edition.features.indexOf("people.invite") !== -1;
    const canManageUsers = this.$rootScope.hasPermission("ManageUsers");

    const selectAssigneeById = (id: string): void => {
      scope.edit = false;
      scope.assignee = scope.assigneesIdMap[id];
      if (id !== ctrl.$viewValue) {
        ctrl.$setViewValue(id);
        scope.selected({ assignee: scope.assignee });
      }
    };

    const createTeam = (name: string): void => {
      const color = randomColor();
      // to do: remove this TS cheat when refactoring
      // id is mandatory in ITeam so we need to create a new interface for a new team but this involves some refactoring
      const team = <ITeam>{
        name,
        description: "",
        color,
        avatar: "users_multiple-11",
        customFields: {},
      };

      this.teamService.createTeam(team).then(
        (newTeam: ITeam) => {
          newTeam.type = "team";

          scope.assigneesIdMap[newTeam.id] = {
            picture: newTeam.picture,
            accountId: newTeam.accountId,
            avatar: newTeam.avatar,
            color: newTeam.color,
            email: null,
            id: newTeam.id,
            name: newTeam.name,
            type: "team",
          };
          selectAssigneeById(newTeam.id);

          this.analyticsService.track("Created Team From Assignee Selector");
        },
        (error) => {
          this.uiErrorHandlingService.handleModal(error);
        }
      );
    };

    const resetSearch = (): void => {
      scope.search = {};
    };

    const openInvitation = (email: string): void => {
      resetSearch();
      scope.invitation = {
        email,
      };
    };

    resetSearch();

    scope.assigneeOptions = (filterByName?: string): IAssigneeOption[] => {
      let options: IAssigneeOption[] = Object.values(scope.assigneesIdMap).filter((a) => {
        const filter = (!scope.type || a.type === scope.type) && (!filterByName || a.name.toLocaleLowerCase().indexOf(filterByName.toLocaleLowerCase()) >= 0);
        return filter && a.isActive !== false;
      });

      if (options.length > maxSearchResults) {
        options = options.slice(0, maxSearchResults - 1);
      }

      if (scope.type !== "team" && canManageUsers && canInvitePeople) {
        const defaultEmail: IInviteOption = {
          email: filterByName,
          type: "invite",
        };
        options.push(defaultEmail);
      }

      if (scope.type !== "user") {
        const defaultTeam: ICreateTeamOption = {
          name: filterByName,
          type: "create_team",
        };
        options.push(defaultTeam);
      }

      return options;
    };

    scope.selectOption = (option: IAssigneeOption): void => {
      switch (option.type) {
        case "invite":
          openInvitation(option.email);
          break;

        case "create_team":
          createTeam(option.name);
          break;

        default: {
          const assignee = option as IAssignee;
          selectAssigneeById(assignee.id);
          break;
        }
      }
    };

    scope.canAssignToMe = (): boolean => {
      return scope.type !== "team";
    };

    scope.assignToMe = (): void => {
      const currentUserId = getCurrentUserId();
      selectAssigneeById(currentUserId);
    };

    scope.changeSelectedAssignee = (): void => {
      if (scope.disabled) {
        return;
      }

      scope.edit = true;
      resetSearch();

      // placeholders are rendered via pug, so we cannot use Angular.js logic here
      const selectorEl = document.querySelector(".gh-assignee-selector");
      if (selectorEl) {
        const defaultPlaceholder = selectorEl.getAttribute("data-placeholder");
        const inptuEl = document.querySelector<HTMLInputElement>(".gh-assignee-selector .gh-input-text");
        if (inptuEl) {
          inptuEl.placeholder = scope.placeholder || defaultPlaceholder;
        }
      }
    };

    scope.undoSelectingAssignee = (): void => {
      scope.edit = false;
      resetSearch();
    };

    scope.openAssigneesList = (): void => {
      this.assigneeSelectorModal.selectOne({ assigneeType: scope.type }).then((assignee) => {
        selectAssigneeById(assignee.id);
      }, noop);
    };

    scope.closeInvitation = (): void => {
      scope.invitation = null;
    };

    scope.userInvited = (user: IUser): void => {
      const color = randomColor();
      scope.assigneesIdMap[user.id] = {
        picture: user.picture,
        isActive: user.isActive,
        accountId: user.accountId,
        avatar: user.avatar,
        color,
        email: user.email,
        id: user.id,
        name: user.name,
        type: "user",
      };
      selectAssigneeById(user.id);

      this.analyticsService.track("Created User From Assignee Selector");
    };

    ctrl.$render = (): void => {
      const assigneeId = ctrl.$modelValue;

      this.$ngRedux.dispatch(this.assigneeActions.getAssignees());

      scope
        .watchUntilAndRejectOnError(
          () => scope.assigneesFetched,
          () => scope.assigneesError
        )
        .then(
          () => {
            if (assigneeId) {
              selectAssigneeById(assigneeId);
            }

            if (scope.edit) {
              scope.changeSelectedAssignee();
            }
          },
          () => this.uiErrorHandlingService.handleModal(scope.assigneesError)
        );
    };
  }

  public static factory(): IDirectiveFactory {
    const directive = (
      $rootScope: IGtmhubRootScopeService,
      teamService: TeamService,
      uiErrorHandlingService: UIErrorHandlingService,
      analyticsService: AnalyticsService,
      assigneeSelectorModal: AssigneeSelectorModal,
      assigneeActions: AssigneeActions,
      $ngRedux: INgRedux
    ) => new AssigneeSelector($rootScope, teamService, uiErrorHandlingService, analyticsService, assigneeSelectorModal, assigneeActions, $ngRedux);
    directive.$inject = ["$rootScope", "TeamService", "UIErrorHandlingService", "AnalyticsService", "AssigneeSelectorModal", "AssigneeActions", "$ngRedux"];
    return directive;
  }
}
